import { isApolloError } from '@apollo/client';
import Button from '@risksmart-app/components/src/button';
import Loading from '@risksmart-app/components/src/loading';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import {
  Document_File_Type_Enum,
  Version_Status_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { isTRPCClientError } from '@trpc/client';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ConfirmChangeRequestModal } from 'src/components/change-requests-preview/ConfirmChangeRequestModal';
import PolicyDocumentStatusBadge from 'src/components/policy-document-status-badge/PolicyDocumentStatusBadge';
import { type ObjectWithContributors, Permission } from 'src/rbac/Permission';

import { useUpdateDocumentVersion } from '@/hooks/mutations/document-file';
import { useGetDocumentFileById } from '@/hooks/queries';
import { useChangeRequests } from '@/hooks/useChangeRequests';
import { mutationResultNotification } from '@/hooks/useMutationResultNotification';
import { toLocalDate } from '@/utils/dateUtils';
import { policyFileUrl } from '@/utils/urls';

type Props = {
  document: ObjectWithContributors;
  documentFileId: string;
};

function DocumentVersionPreview({ document, documentFileId }: Props) {
  const { data, loading: loadingDocument } = useGetDocumentFileById({
    queryArgs: { id: documentFileId },
  });

  const { t } = useTranslation(['common'], {
    keyPrefix: 'documentFiles.versionPreview',
  });
  const navigate = useNavigate();

  const { updateDocumentVersion } = useUpdateDocumentVersion();

  const documentVersion = data?.document_file[0];
  const documentIsDraft =
    documentVersion?.Status === Version_Status_Enum.Draft ||
    documentVersion?.Status === Version_Status_Enum.PendingApproval;
  const objectWithApprovals = documentVersion
    ? {
        ...documentVersion,
        owners: documentVersion.parent?.owners ?? [],
        ownerGroups: documentVersion.parent?.ownerGroups ?? [],
      }
    : undefined;

  const [showConfirmChangeRequest, setShowConfirmChangeRequest] =
    useState(false);
  const { pendingChangeRequests, loading: loadingChangeRequests } =
    useChangeRequests(objectWithApprovals);
  const { addNotification } = useNotifications();
  const loading = loadingDocument || loadingChangeRequests;

  const handlePublish = async (
    confirm?: boolean,
    requesterComment?: string
  ) => {
    if (!documentVersion) {
      return;
    }
    await mutationResultNotification({
      addNotification,
      successMessageKey: 'update_success_message',
      entityName: 'Document Version',
      asyncAction: async () => {
        const sharedFields = {
          Id: documentVersion.Id,
          LatestModifiedAtTimestamp: documentVersion.ModifiedAtTimestamp,
          Version: documentVersion.Version,
          Status: Version_Status_Enum.Published,
          Summary: documentVersion.Summary,
          ReasonForReview: documentVersion.ReasonForReview,
          ReviewedBy: documentVersion.ReviewedBy,
          ReviewDate: documentVersion.ReviewDate,
          NextReviewDate: documentVersion.NextReviewDate,
          CustomAttributeData: documentVersion.CustomAttributeData,
        };
        const options = {
          confirmChangeRequest: confirm,
          requesterComment,
        };
        try {
          switch (documentVersion.Type) {
            case Document_File_Type_Enum.Link:
              await updateDocumentVersion(
                {
                  ...sharedFields,
                  Type: Document_File_Type_Enum.Link,
                  Link: documentVersion.Link ?? '',
                },
                options
              );
              break;
            case Document_File_Type_Enum.Html:
              await updateDocumentVersion(
                {
                  ...sharedFields,
                  Type: Document_File_Type_Enum.Html,
                  Content: documentVersion.Content ?? '',
                },
                options
              );
              break;
            case Document_File_Type_Enum.File:
              await updateDocumentVersion(
                {
                  ...sharedFields,
                  Type: Document_File_Type_Enum.File,
                  FileId: documentVersion.FileId ?? '',
                },
                options
              );
              break;
          }
        } catch (error) {
          let message = '';
          if (error instanceof Error && isApolloError(error)) {
            message = error.graphQLErrors.map((er) => er.message).join(' ');
          } else if (isTRPCClientError(error)) {
            message = error.message;
          }
          if (message.includes('You need to create a change request')) {
            setShowConfirmChangeRequest(true);

            return false;
          }
          throw error;
        }

        return true;
      },
    })({});
  };

  const handleOpen = useCallback(() => {
    if (!documentVersion) {
      return;
    }
    if (
      documentVersion.Type === Document_File_Type_Enum.Link &&
      documentVersion.Link
    ) {
      window.open(documentVersion.Link, '_blank');
    } else if (
      documentVersion.FileId ||
      documentVersion.Type === Document_File_Type_Enum.Html
    ) {
      navigate(policyFileUrl(document.Id, documentVersion.Id));
    }
  }, [document.Id, documentVersion, navigate]);

  if (!documentVersion) {
    return null;
  }

  return loading ? (
    <Loading />
  ) : (
    <>
      {showConfirmChangeRequest && (
        <ConfirmChangeRequestModal
          onDismiss={() => setShowConfirmChangeRequest(false)}
          onConfirm={(requesterComment) =>
            handlePublish(true, requesterComment)
          }
        />
      )}
      <div
        className={`p-5 bg-off_white rounded-md flex flex-col gap-4 justify-items-start`}
      >
        <h3 className={'m-0'}>
          {documentIsDraft ? t('latestDraftTitle') : t('latestPublishedTitle')}
        </h3>
        <div
          className={
            'p-4 bg-white border-grey150 border-solid border-2 rounded-md flex flex-col gap-2'
          }
        >
          <div className={'flex justify-between'}>
            <h3 className={'m-0'}>
              {t('version', { version: documentVersion.Version })}
            </h3>
            <PolicyDocumentStatusBadge
              item={documentVersion}
              changeRequests={documentVersion.changeRequests}
            />
          </div>
          <p className={'text-grey600 m-0 mb-3'}>
            <strong>
              {t('created')}
              {':'}
            </strong>{' '}
            {toLocalDate(documentVersion.CreatedAtTimestamp)}
          </p>
          <div className={'flex gap-2'}>
            {!documentIsDraft ? (
              <Button
                variant={'primary'}
                formAction={'none'}
                onClick={handleOpen}
              >
                {t('view')}
              </Button>
            ) : null}
            {documentIsDraft && (
              <>
                <Button
                  formAction={'none'}
                  onClick={() => navigate(`files/update/${documentVersion.Id}`)}
                  variant={'primary'}
                >
                  {t('view')}
                </Button>
                {pendingChangeRequests.length < 1 && (
                  <Permission
                    permission={'update:document_file'}
                    parentObject={document}
                  >
                    <Button onClick={() => handlePublish()}>{'Publish'}</Button>
                  </Permission>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default DocumentVersionPreview;
